<template>
    <b-form-row v-if="patient">
        <b-col>
            <b-form-row class="align-items-center">
                <b-col cols="5">
                    <SearchComponent v-model="selectedAllergy" :label="`Allergy Search`"
                        search-u-r-l="/Allergy/GetGSDDAllergies" :disabled="disable" clearable />
                </b-col>
                <b-col cols="5">
                    <b-form-group label="Reaction:">
                        <b-input v-model="reaction" maxlength="100" :disabled="disable" />
                    </b-form-group>
                </b-col>
                <b-col cols="2">
                    <checkbox v-model="patient.nkda" :disabled="(disable || disableNkda)" @input="nkdaChanged">
                        NKDA
                    </checkbox>
                </b-col>
            </b-form-row>

            <b-button @click="addAllergy" v-if="!disable" variant="success">
                Add Allergy
            </b-button>

            <b-table-simple v-if="!!patientAllergies.length">
                <b-thead>
                    <b-tr>
                        <b-th>Allergen</b-th>
                        <b-th>Type</b-th>
                        <b-th>Reaction</b-th>
                        <b-th>Added Date</b-th>
                        <b-th class="remove"></b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="allergy in patientAllergies" :key="allergy.allergyName">
                        <b-td>{{ allergy.allergyName }}</b-td>
                        <b-td>
                            {{
                                allergy.allergyType == 0 ? 'Class' :
                                    allergy.allergyType == 1 ? 'Ingredient' :
                                        'Drug'
                            }}
                        </b-td>
                        <b-td>
                            {{ allergy.reaction }}
                        </b-td>
                        <b-td>
                            {{ allergy.created | formatDate("LLL") }}
                        </b-td>
                        <b-td>
                            <b-button variant="danger" size="sm" @click="RemoveAllergy(allergy)">Remove</b-button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>

            <div v-if="patient && patient.id && requireRemovalReason" class="mt-2">
                <b-button variant="link" size="sm" class="px-0" @click="toggleArchived">
                    {{ showArchived ? 'Hide' : 'Show' }} removed allergies ({{ archivedAllergies.length }})
                </b-button>
                <b-table-simple v-if="showArchived && archivedAllergies.length > 0" small>
                    <b-thead>
                        <b-tr>
                            <b-th>Allergen</b-th>
                            <b-th>Reaction</b-th>
                            <b-th>Removed Date</b-th>
                            <b-th>Removed By</b-th>
                            <b-th>Reason</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(archived, idx) in archivedAllergies" :key="`archived-${idx}`">
                            <b-td>{{ archived.allergyName }}</b-td>
                            <b-td>{{ archived.reaction }}</b-td>
                            <b-td>{{ archived.removed | formatDate("LLL") }}</b-td>
                            <b-td>{{ archived.removedBy }}</b-td>
                            <b-td>{{ archived.removalReason }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
                <div v-else-if="showArchived" class="text-muted small">No removed allergies.</div>
            </div>

            <RemovalReasonModal ref="reasonModal" />
        </b-col>
    </b-form-row>
</template>

<script lang="ts">
import { Component, Watch, Prop, Vue, Ref } from 'vue-property-decorator'
import { DatePicker } from "element-ui";
import axios from 'axios';
import { NotificationOptions } from "@/util/NotificationOptionsPresets"
import { Patient } from '@/models/Patient';
import SearchComponent from '@/components/SearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import RemovalReasonModal from '@/components/Patient/RemovalReasonModal.vue';
import { GSDDPatientAllergy, AllergyRemovalReason } from "@/models/GSDD";

@Component({
    name: "GSDDAllergyComponent",
    components: {
        SearchComponent,
        NewClearSaveButtons,
        AddressFormFields,
        Checkbox,
        RemovalReasonModal,
        [DatePicker.name]: DatePicker
    }
})
export default class GSDDAllergyComponent extends Vue {
    @Prop({ default: null }) private patient!: Patient;
    @Prop({ default: false }) private disable!: boolean
    @Prop({ default: () => [] }) private value!: GSDDPatientAllergy[];

    @Ref('reasonModal') private reasonModal!: RemovalReasonModal;

    //private patientAllergies: GSDDPatientAllergy[] = []
    private selectedAllergy: GSDDPatientAllergy = new GSDDPatientAllergy();
    private reaction: string = "";

    protected requireRemovalReason = false;
    protected showArchived = false;
    protected archivedAllergies: GSDDPatientAllergy[] = [];
    // Archived list is lazy-loaded on first expand and cached until the patient changes.
    private archivedLoaded = false;

    // Collected removal reasons for allergies removed since the last save; read by the parent
    // form when it POSTs the reconcile so the backend can persist each reason. Public by design.
    public removalReasons: AllergyRemovalReason[] = [];

    get patientAllergies(): GSDDPatientAllergy[] {
        return this.value;
    }

    set patientAllergies(val: GSDDPatientAllergy[]) {
        this.$emit('input', val);
    }

    get disableNkda(): boolean {
        return this.patientAllergies.length > 0;
    }

    created() {
        this.getPatientGSDDAllergies();
        this.fetchRemovalReasonConfig();
    }

    async fetchRemovalReasonConfig() {
        try {
            const resp = await axios.get<boolean>('/Configuration/RequireReasonForPatientMacRemoval');
            this.requireRemovalReason = resp.data === true;
        } catch (err) {
            // Default to prompting on a transient failure: the server enforces the rule independently,
            // so skipping the prompt would only produce a confusing 400 the user can't act on.
            console.error("Error loading removal-reason config", err);
            this.requireRemovalReason = true;
        }
    }

    async toggleArchived() {
        this.showArchived = !this.showArchived;
        if (this.showArchived && !this.archivedLoaded) await this.fetchArchivedAllergies();
    }

    async fetchArchivedAllergies() {
        if (!this.patient?.id) return;
        try {
            const resp = await axios.get<GSDDPatientAllergy[]>(`/Allergy/GetArchivedGSDDAllergiesForPatient/${this.patient.id}`);
            this.archivedAllergies = resp.data;
            this.archivedLoaded = true;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    getPatientGSDDAllergies() {
        if (!this.patient?.id) return;

        axios.get(`/Allergy/GetGSDDAllergiesForPatient/${this.patient.id}`)
            .then(response => {
                this.patientAllergies = response.data.sort((a: GSDDPatientAllergy, b: GSDDPatientAllergy) => {
                    if (!a.created && !b.created) return 0;
                    if (!a.created) return 1;
                    if (!b.created) return -1;
                    return new Date(b.created).getTime() - new Date(a.created).getTime();
                });
            }).catch(error => {
                console.error("Error while loading allergies", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    @Watch('patient.id') onPatientChanged(value: Patient | null, oldValue: Patient | null) {
        this.clearComponent();
        this.getPatientGSDDAllergies();
        // Invalidate the archived cache so a later expand re-fetches for the new patient.
        this.archivedLoaded = false;
        this.archivedAllergies = [];
        this.showArchived = false;
    }

    nkdaChanged(val: boolean) {
        if (val) {
            this.patientAllergies = [];
        }
    }

    addAllergy() {
        if (!this.selectedAllergy?.allergyName) {
            this.$notification(NotificationOptions.error("Please select an Allergy."));
            return;
        }

        if (this.patientAllergies.includes(this.selectedAllergy)) {
            this.$notification(NotificationOptions.error("This Allergy has already been added."));
            return;
        }

        this.selectedAllergy.patientId = this.patient.id as number;
        this.selectedAllergy.reaction = this.reaction;
        this.selectedAllergy.created = new Date();
        this.patientAllergies.unshift(this.selectedAllergy);
        this.patient.nkda = false;
        this.reaction = "";
    }

    async RemoveAllergy(allergy: any) {
        // Only removing an allergy that is already persisted needs a reason; a not-yet-saved
        // addition (no created date) can be dropped freely.
        if (this.requireRemovalReason && this.patient?.id && allergy.created) {
            const reason = await this.reasonModal.prompt({
                itemLabel: allergy.allergyName,
                required: true,
                title: 'Remove Allergy',
            });
            if (reason === null) return;

            this.removalReasons = [
                ...this.removalReasons.filter(r => !(r.allergyType === allergy.allergyType && r.allergyId === allergy.allergyId && r.reaction === allergy.reaction)),
                { allergyType: allergy.allergyType, allergyId: allergy.allergyId, reaction: allergy.reaction, reason },
            ];
        }

        this.patientAllergies = this.patientAllergies.filter(
            item => !(item.allergyType === allergy.allergyType && item.allergyId === allergy.allergyId && item.reaction === allergy.reaction));
    }

    clearComponent() {
        this.patientAllergies = new Array<GSDDPatientAllergy>();
        this.selectedAllergy = new GSDDPatientAllergy();
        this.reaction = "";
        this.removalReasons = [];
    }

}
</script>

<style scoped lang="scss">
th.remove {
    width: 110px;
}
</style>
